<template>
  <el-row v-if="process" id="background-process-progress" class="background-process-progress">
    <el-col :span="24">
      <el-alert :type="alertType" :closable="isFinished" @close="handleDismiss" show-icon>
        <template slot="title">
          <span class="background-process-title">{{ headline }}</span>
        </template>
        <div class="background-process-body">
          <el-progress
            :percentage="percentage"
            :status="progressStatus"
            :stroke-width="10"
            id="background-process-bar"
          ></el-progress>

          <div class="background-process-counters">
            <span id="background-process-counts">
              {{ process.processed || 0 }} / {{ process.total || 0 }}
            </span>
            <span v-if="process.succeeded > 0" class="background-process-ok">
              {{ $t('invoices.backgroundProcess.sent') }}: {{ process.succeeded }}
            </span>
            <span v-if="process.skipped > 0">
              {{ $t('invoices.backgroundProcess.skipped') }}: {{ process.skipped }}
            </span>
            <span v-if="process.failed > 0" class="background-process-failed">
              {{ $t('invoices.backgroundProcess.failed') }}: {{ process.failed }}
            </span>
            <span v-if="process.requestedBy" class="background-process-meta">
              {{ $t('invoices.backgroundProcess.requestedBy') }}: {{ process.requestedBy }}
            </span>
            <span v-if="process.filters" class="background-process-meta">{{ process.filters }}</span>
          </div>

          <!-- Said out loud rather than hidden behind a spinner: a run whose instance died would
               otherwise animate forever and look healthy. -->
          <div v-if="process.stale" class="background-process-stale">
            {{ $t('invoices.backgroundProcess.stale', { minutes: staleMinutes }) }}
          </div>

          <div v-if="process.errorMessage" class="background-process-error">
            {{ process.errorMessage }}
          </div>

          <div class="background-process-actions">
            <el-button
              v-if="!isFinished && !process.cancelRequested"
              id="background-process-cancel-button"
              size="mini"
              @click="handleCancel"
            >{{ $t('invoices.backgroundProcess.cancel') }}</el-button>
            <span v-if="process.cancelRequested && !isFinished" class="background-process-meta">
              {{ $t('invoices.backgroundProcess.cancelling') }}
            </span>
            <el-button
              v-if="process.failed > 0"
              id="background-process-show-failures-button"
              size="mini"
              type="text"
              @click="handleShowFailures"
            >{{ $t('invoices.backgroundProcess.showFailures') }}</el-button>
          </div>
        </div>
      </el-alert>

      <el-dialog
        :title="$t('invoices.backgroundProcess.failuresTitle')"
        :visible.sync="failuresVisible"
        width="60%"
        id="background-process-failures-dialog"
        append-to-body
      >
        <el-table :data="failures" size="mini" max-height="360" id="background-process-failures-table">
          <el-table-column
            prop="itemLabel"
            :label="$t('invoices.backgroundProcess.invoiceNumber')"
            width="140"
          ></el-table-column>
          <el-table-column
            prop="errorMessage"
            :label="$t('invoices.backgroundProcess.reason')"
          ></el-table-column>
        </el-table>
      </el-dialog>
    </el-col>
  </el-row>
</template>

<script>
import { Message } from 'element-ui';
import { mapGetters } from 'vuex';
import backgroundProcessService from '@/api/backgroundProcessService';

/**
 * Shows what a background run is doing, by asking the server rather than remembering.
 *
 * The screen used to have nothing to show: the request answered "success" before the work started.
 * Anything kept in this component's memory would be just as blind, because billing runs on two
 * instances and the browser talks to whichever one the balancer picks - so every number here comes
 * from the run's own record.
 *
 * On mount it asks whether something is already running and attaches to it. That is what makes F5
 * harmless and lets two people watch the same run.
 */
export default {
  name: 'BackgroundProcessProgress',
  props: {
    processName: {
      type: String,
      required: true
    },
    pollSeconds: {
      type: Number,
      default: 3
    },
    /**
     * Stops the polling from running for ever if a run never writes a terminal state. Twenty minutes
     * at three seconds is well past any run measured so far.
     */
    maxPolls: {
      type: Number,
      default: 400
    }
  },
  data() {
    return {
      process: null,
      failures: [],
      failuresVisible: false,
      timer: null,
      polls: 0,
      // Backs off instead of hammering a server that is already unhappy, and gives up rather than
      // leaving a stale bar on screen pretending to be live.
      consecutiveErrors: 0,
      dismissed: false
    };
  },
  computed: {
    ...mapGetters(['invoices']),
    isFinished() {
      return Boolean(this.process && this.process.finished);
    },
    percentage() {
      if (!this.process || !this.process.total) {
        return 0;
      }
      const done = Math.min(this.process.processed || 0, this.process.total);
      return Math.round((done / this.process.total) * 100);
    },
    progressStatus() {
      if (!this.isFinished) {
        return null;
      }
      if (this.process.status === 'FINISHED' && !this.process.failed) {
        return 'success';
      }
      return this.process.status === 'FINISHED' ? 'warning' : 'exception';
    },
    alertType() {
      if (!this.isFinished) {
        return this.process && this.process.stale ? 'warning' : 'info';
      }
      if (this.process.status !== 'FINISHED') {
        return 'error';
      }
      return this.process.failed > 0 ? 'warning' : 'success';
    },
    staleMinutes() {
      if (!this.process || !this.process.secondsSinceHeartbeat) {
        return 0;
      }
      return Math.floor(this.process.secondsSinceHeartbeat / 60);
    },
    headline() {
      if (!this.process) {
        return '';
      }
      const key = this.isFinished ? `status.${this.process.status}` : 'status.RUNNING';
      return this.$t(`invoices.backgroundProcess.${key}`);
    }
  },
  watch: {
    /** The button that starts a run leaves its id here; this is how the banner learns about it. */
    'invoices.lastBackgroundProcessId': function watchLastProcessId(processId) {
      this.follow(processId);
    }
  },
  mounted() {
    this.attachToCurrent();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    /** Asks the server if a run is already in progress, so opening or refreshing the page finds it. */
    attachToCurrent() {
      backgroundProcessService.getCurrent(this.processName).then((response) => {
        // 204 with no body means nothing is running - not an error, just nothing to show.
        if (response && response.id) {
          this.process = response;
          this.dismissed = false;
          this.startPolling();
        }
      }).catch(() => {
        // Silent on purpose: not being able to check is no reason to interrupt somebody who came
        // here to look at invoices.
      });
    },
    /** Called by the parent with the id the posting request returned. */
    follow(processId) {
      if (!processId) {
        return;
      }
      this.dismissed = false;
      this.polls = 0;
      this.consecutiveErrors = 0;
      this.fetch(processId);
      this.startPolling();
    },
    startPolling() {
      this.stopPolling();
      this.timer = setInterval(this.tick, this.pollSeconds * 1000);
    },
    stopPolling() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    tick() {
      this.polls += 1;
      if (this.polls > this.maxPolls || !this.process || this.isFinished) {
        this.stopPolling();
        return;
      }
      this.fetch(this.process.id);
    },
    fetch(processId) {
      backgroundProcessService.getById(processId).then((response) => {
        this.consecutiveErrors = 0;
        if (this.dismissed) {
          return;
        }
        this.process = response;
        if (this.process.finished) {
          this.stopPolling();
          this.$emit('finished', this.process);
        }
      }).catch(() => {
        this.consecutiveErrors += 1;
        if (this.consecutiveErrors >= 5) {
          // Stops rather than showing numbers that no longer come from anywhere.
          this.stopPolling();
        }
      });
    },
    handleCancel() {
      backgroundProcessService.cancel(this.process.id).then((response) => {
        this.process = response;
        Message.info(this.$t('invoices.backgroundProcess.cancelRequested'));
      }).catch(() => {});
    },
    handleShowFailures() {
      backgroundProcessService.getItems(this.process.id, 'FAILED').then((response) => {
        this.failures = response || [];
        this.failuresVisible = true;
      }).catch(() => {});
    },
    handleDismiss() {
      this.dismissed = true;
      this.stopPolling();
      this.process = null;
    }
  }
};
</script>

<style scoped>
.background-process-progress {
  margin-bottom: 8px;
}

.background-process-title {
  font-weight: 600;
}

.background-process-body {
  padding-top: 4px;
}

.background-process-counters {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding-top: 6px;
  font-size: 12px;
}

.background-process-ok {
  color: #67c23a;
}

.background-process-failed {
  color: #f56c6c;
  font-weight: 600;
}

.background-process-meta {
  color: #909399;
}

.background-process-stale,
.background-process-error {
  padding-top: 6px;
  font-size: 12px;
  color: #e6a23c;
}

.background-process-actions {
  padding-top: 6px;
}
</style>
